import { fetchJSON as fetchAPI } from './client';

export type DecisionDashboard = {
  tenant_id: string;
  workspace_id?: string;
  total_decisions: number;
  pending_approvals: number;
  executed_decisions: number;
  open_recommendations: number;
  active_optimizations: number;
  running_simulations: number;
  active_agents: number;
  avg_confidence: number;
  high_risk_decisions: number;
  autonomous_executions: number;
  generated_at: string;
  recent_activity?: DecisionActivity[];
  platform_health?: { module: string; status: string; latency_ms?: number }[];
};

export type Decision = {
  id: string;
  tenant_id: string;
  workspace_id?: string;
  title: string;
  description?: string;
  domain: string;
  status: string;
  confidence: number;
  score: number;
  execution_mode: string;
  explanation?: string;
  agent_id?: string;
  created_at: string;
  updated_at: string;
};

export type Recommendation = {
  id: string;
  tenant_id: string;
  domain: string;
  title: string;
  description?: string;
  priority: string;
  confidence: number;
  impact: number;
  status: string;
  explanation?: string;
  created_at: string;
};

export type Optimization = {
  id: string;
  tenant_id: string;
  domain: string;
  title: string;
  status: string;
  improvement_pct: number;
  confidence: number;
  created_at: string;
};

export type SimulationResult = {
  scenario_id: string;
  outcome: Record<string, number>;
  confidence: number;
  sensitivity?: Record<string, number>;
  explanation?: string;
  completed_at: string;
};

export type SimulationScenario = {
  id: string;
  tenant_id: string;
  name: string;
  type: string;
  status: string;
  created_at: string;
};

export type Agent = {
  id: string;
  tenant_id: string;
  role: string;
  name: string;
  description?: string;
  status: string;
  capabilities?: string[];
  created_at: string;
};

export type RiskAssessment = {
  id: string;
  tenant_id: string;
  decision_id?: string;
  category: string;
  score: number;
  level: string;
  mitigation?: string;
  assessed_at: string;
};

export type DecisionActivity = {
  id: string;
  tenant_id: string;
  resource_id?: string;
  action: string;
  message: string;
  created_at: string;
};

const ROOT_API = import.meta.env.VITE_DECISION_API ?? '/api/v1';

function fetchRoot<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(ROOT_API, path, init);
}

function fetchDecision<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(ROOT_API, `/decision${path}`, init);
}

export const decisionApi = {
  dashboard: (workspaceId?: string) =>
    fetchDecision<DecisionDashboard>(`/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  listDecisions: (workspaceId?: string) =>
    fetchRoot<Decision[]>(`/decisions${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  createDecision: (body: {
    title: string;
    domain: string;
    workspace_id?: string;
    description?: string;
    execution_mode?: string;
    confidence?: number;
  }) => fetchRoot<Decision>('/decisions', { method: 'POST', body: JSON.stringify(body) }),

  listRecommendations: (workspaceId?: string) =>
    fetchRoot<Recommendation[]>(`/recommendations${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  listOptimizations: (workspaceId?: string) =>
    fetchRoot<Optimization[]>(`/optimizations${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  createSimulation: (body: { name: string; type?: string; parameters?: Record<string, number> }) =>
    fetchRoot<SimulationResult>('/simulations', { method: 'POST', body: JSON.stringify(body) }),

  listSimulations: (workspaceId?: string) =>
    fetchRoot<SimulationScenario[]>(`/simulations${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  listAgents: () => fetchRoot<Agent[]>('/agents'),

  createAgent: (body: { name: string; role?: string; description?: string }) =>
    fetchRoot<Agent>('/agents', { method: 'POST', body: JSON.stringify(body) }),

  listRisk: () => fetchDecision<RiskAssessment[]>('/risk'),

  listActivity: () => fetchDecision<DecisionActivity[]>('/activity'),
};
